import { App, type Plugin } from 'vue';
import { createI18n as baseCreateI18n } from 'vue-i18n';

import { getInitialLanguage, OPTIONS as defaultOption } from '@bv/defaults/i18n';
import { type I18nOptions } from '@bv/plugins';
import { switchLanguage, setI18nInstance, deepMergeObjects } from '@bv/utils';

/**
 * Creates a configured i18n Vue plugin instance
 *
 * @example
 * const i18n = createI18n({
 *   locale: 'en',
 *   messages: { en: { hello: 'Hello' } }
 * })
 * app.use(i18n)
 *
 * @param {I18nOptions} options - Base i18n configuration
 * @returns {PluginInstallFunction} Vue plugin installer
 */
export const createI18n = (options: I18nOptions & { setLocale?: boolean }): Plugin => {
  const mergedMessages = deepMergeObjects(
    defaultOption.messages || {},
    options.messages || ({} as I18nOptions['messages'][]),
  );
  return {
    install: (app: App, installOptions: I18nOptions = {}): void => {
      const mergedOptions = deepMergeObjects(defaultOption, [options, { messages: mergedMessages }]);
      const mergedInstallOptions = deepMergeObjects(mergedOptions, installOptions);

      const instance = baseCreateI18n(mergedInstallOptions);

      setI18nInstance(instance);

      if (options.setLocale !== false) {
        switchLanguage(getInitialLanguage().code);
      }

      app.use(instance);
      app.config.globalProperties.$i18n = instance.global;
    },
  };
};

export { switchLanguage };
